import { Grid } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCVParsedTotalProcessedCountAnalytics,
  getCVParserStatusAdminWithIntervals,
  ICVUploadAnalytics,
} from 'wisestep-api';
import { WsTitleBanner, getCurrentZoneAbbr, WsColors } from 'wisestep-base';

import { getSumOfColumnRecords } from '../../../features/composableAnalytics/BaseCharts/clientListing/helper/payloadsAndTypesHelper';
import CountWithTextCard from '../components/countWithTextCard';

import CVParsedRecord from './component/CVParsedRecord';
import CVUpload30DaysTable from './component/CVUpload30DaysTable';
import CVUploadRecord from './component/CVUploadRecord';
import CVUploadWithStatus from './component/CVUploadWithStatus';

export const subtractYears = (years) => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - years);
  return date;
};
interface ICVParsedcountType {
  parsedCount: string;
  type: string;
  hour: string;
}

interface ICVParserStatusAdmin {
  tableData: Record<string, unknown>[];
  type: string;
  hour: string;
}

export default function CVUploadAnalytics() {
  const navigate = useNavigate();
  const [cvParserDataAdmin, setCvParserDataAdmin] = useState<ICVParserStatusAdmin[]>([]);
  const [cvParserRecordData, setCvParserRecordData] = useState<ICVParsedcountType[]>([]);
  const [cvParser24HourCount, setCvParser24HourCount] = useState<number>(0);
  const cvParserHourList = useMemo(
    () => [
      { hour: '1', type: 'h' },
      { hour: '8', type: 'h' },
      { hour: '24', type: 'h' },
      { hour: '7', type: 'd' },
      { hour: '30', type: 'd' },
    ],
    []
  );
  useEffect(() => {
    const cvParsedStatusCounts = async () => {
      const payload: ICVUploadAnalytics[] = [];
      cvParserHourList.map((array) => {
        payload.push({
          timeDifference: array.hour,
          type: array.type,
        });
      });
      const response = await getCVParsedTotalProcessedCountAnalytics(payload);
      setCvParserRecordData(response.data);
      const count24Hour = response.data.find((item) => item.type === 'h' && item.hour === '24');
      setCvParser24HourCount(count24Hour?.parsedCount ?? 0);
    };
    cvParsedStatusCounts();
  }, [cvParser24HourCount, cvParserHourList]);

  const filterQuery: ICVUploadAnalytics = {
    utcOffset: getCurrentZoneAbbr(),
    timeDifference: '1',
    utcOffsetInHour: 5,
    utcOffsetInMinute: 30,
  };
  const date = new Date();
  filterQuery.fromDate = subtractYears(1);
  filterQuery.toDate = date;

  useEffect(() => {
    const data = async () => {
      const payload: ICVUploadAnalytics[] = [];
      cvParserHourList.map((array) => {
        payload.push({
          timeDifference: array.hour,
          type: array.type,
        });
      });
      const response = await getCVParserStatusAdminWithIntervals(payload);
      setCvParserDataAdmin(response.data);
    };
    data();
  }, [cvParserHourList]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'id',
      hide: true,
    },
    {
      field: 'workspaceName',
      headerName: `Workspace Name`,
      width: 200,
    },
    {
      field: 'total',
      headerName: 'Upload',
      width: 150,
    },
    {
      field: 'success',
      headerName: `Success`,
      width: 150,
    },
    {
      field: 'processing',
      headerName: `InQueue`,
      width: 150,
    },
    {
      field: 'failed',
      headerName: 'Failure',
      width: 150,
    },
    {
      field: 'reasonForFailure',
      headerName: 'Reason For Failure',
      width: 650,
    },
  ];

  return (
    <Grid>
      <WsTitleBanner title="CV Upload Analytics" goBack={() => navigate('/analytics/')} />
      <Grid p={2} bgcolor={WsColors.White}>
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={4}>
            <CVUploadRecord payload={filterQuery} hour={'1'} />
          </Grid>
          <Grid item xs={4}>
            <CVUploadRecord payload={filterQuery} hour={'8'} />
          </Grid>
          <Grid item xs={4}>
            <CVUploadRecord payload={filterQuery} hour={'24'} />
          </Grid>
        </Grid>
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }} pt={2}>
          <Grid item xs={12}>
            <CVUpload30DaysTable payload={filterQuery} />
          </Grid>
        </Grid>
        <Grid
          container
          rowSpacing={2}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          style={{ padding: '30px' }}
          pb={4}
        >
          {cvParserRecordData.map((item, index) => (
            <Grid item xs={2} key={index}>
              <CVParsedRecord
                count={item.parsedCount}
                hour={item.hour}
                titleText={''}
                type={item.type}
              />
            </Grid>
          ))}
        </Grid>
        <Grid container rowSpacing={2}>
          {cvParserDataAdmin.map((item, index: number) => (
            <Grid item xs={12} key={index}>
              <CVUploadWithStatus
                columns={getSumOfColumnRecords(item.tableData, columns, [
                  'id',
                  'workspaceName',
                  'reasonForFailure',
                ])}
                rows={item.tableData}
                hour={item.hour}
                type={item.type}
              />
            </Grid>
          ))}
        </Grid>
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={12}>
            <Grid item xs={12} style={{ height: '100%' }}>
              <CountWithTextCard
                count={cvParser24HourCount}
                text={`CVs parsed status in last 24 hours `}
                fullHeight={true}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
